import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { GeocodedAddress } from '../../types/address';

interface StreetInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (address: GeocodedAddress | null) => void;
  selectedAddress: GeocodedAddress | null;
  suggestions: GeocodedAddress[];
  isLoading: boolean;
  error?: string | null;
  disabled?: boolean;
}

export function StreetInput({
  value,
  onChange,
  onAddressSelect,
  selectedAddress,
  suggestions,
  isLoading,
  error,
  disabled = false
}: StreetInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [pendingAutofill, setPendingAutofill] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef(value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const lastValue = lastValueRef.current;
    lastValueRef.current = value;
    if (lastValue.length <= 2 && value.length >= 5 && !selectedAddress) {
      setPendingAutofill(true);
    }
  }, [value, selectedAddress]);

  useEffect(() => {
    if (pendingAutofill && suggestions.length > 0 && !selectedAddress) {
      setIsOpen(true);
      setPendingAutofill(false);
    }
  }, [pendingAutofill, suggestions, selectedAddress]);

  useEffect(() => {
    if (pendingAutofill) {
      const timeout = setTimeout(() => setPendingAutofill(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [pendingAutofill]);

  useEffect(() => {
    if (!selectedAddress && !pendingAutofill && suggestions.length > 0 && value.length >= 3) {
      setIsOpen(true);
      setActiveIndex(-1);
    } else if (suggestions.length === 0) {
      setIsOpen(false);
    }
  }, [suggestions, value, selectedAddress, pendingAutofill]);

  const handleSelect = (address: GeocodedAddress) => {
    onAddressSelect(address);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleClear = () => {
    onAddressSelect(null);
    onChange('');
    setPendingAutofill(false);
    lastValueRef.current = '';
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && suggestions.length > 0 && e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
      setActiveIndex(0);
      return;
    }
    if (!isOpen || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < suggestions.length) handleSelect(suggestions[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  const listboxId = 'street-suggestions-listbox';
  const hasError = !!error;

  if (selectedAddress) {
    const parts = selectedAddress.display_name.split(',').map(p => p.trim());
    const mainAddress = parts[0] || selectedAddress.display_name;
    const locationParts = parts.slice(-2).join(', ');

    return (
      <div ref={containerRef}>
        <label className="block text-[15px] font-semibold mb-2">Calle y Numero</label>
        <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border-2 border-green-500 rounded-lg">
          <div className="shrink-0 w-9 h-9 rounded-full bg-green-500 text-white flex items-center justify-center">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <span className="block text-[15px] font-medium text-green-800 break-words">{mainAddress}</span>
            {parts.length > 1 && (
              <span className="block text-[13px] text-green-600 mt-0.5">{locationParts}</span>
            )}
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-black/5 hover:text-red-500 transition-all"
              aria-label="Cambiar dirección"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <label htmlFor="street-input" className="block text-[15px] font-semibold mb-2">
        Calle y Numero
        <span className="text-gray-400 font-normal ml-2 text-sm">(Ej: Av. Colon 1234)</span>
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          id="street-input"
          type="text"
          name="address"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && !pendingAutofill && setIsOpen(true)}
          disabled={disabled}
          autoComplete={value.length === 0 ? "street-address" : "off"}
          aria-autocomplete="list"
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={activeIndex >= 0 ? `suggestion-${activeIndex}` : undefined}
          aria-expanded={isOpen}
          aria-invalid={hasError}
          aria-describedby={hasError ? 'street-error' : undefined}
          className={`w-full px-4 py-3 text-base border-2 rounded-lg outline-none transition-colors pr-10 ${
            hasError
              ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
              : 'border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200'
          }`}
          placeholder="Ingresa tu direccion..."
        />

        {(isLoading || pendingAutofill) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
            <svg className="w-5 h-5 text-green-500 animate-spin" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        )}
      </div>

      {hasError && (
        <p id="street-error" className="mt-2 text-sm text-red-600" role="alert">{error}</p>
      )}

      {isOpen && suggestions.length > 0 && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label="Sugerencias de direccion"
          className="absolute z-[1000] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-[280px] overflow-y-auto list-none p-0 m-0"
        >
          {suggestions.map((suggestion, index) => {
            const isPrivate = suggestion.is_private_neighborhood;
            return (
              <li
                key={`${suggestion.lat}-${suggestion.lon}-${index}`}
                id={`suggestion-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                onClick={() => handleSelect(suggestion)}
                onMouseEnter={() => setActiveIndex(index)}
                className={`px-4 py-3.5 cursor-pointer transition-colors ${
                  index !== suggestions.length - 1 ? 'border-b border-gray-100' : ''
                } ${
                  index === activeIndex
                    ? (isPrivate ? 'bg-orange-50' : 'bg-green-50')
                    : 'bg-transparent'
                }`}
              >
                <div className="flex items-start gap-3">
                  {isPrivate ? (
                    <svg className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="m-0 font-medium text-gray-800 text-[15px]">{suggestion.display_name}</p>
                      {isPrivate && (
                        <span className="inline-block px-2 py-0.5 bg-orange-500 text-white text-[11px] font-semibold rounded uppercase tracking-wide">
                          Barrio Cerrado
                        </span>
                      )}
                    </div>
                    {suggestion.address?.city && (
                      <p className="m-0 mt-1 text-[13px] text-gray-500">{suggestion.address.city}</p>
                    )}
                    {isPrivate && suggestion.parsed_country_data && (
                      <p className="m-0 mt-1 text-xs text-orange-500">
                        {suggestion.parsed_country_data.block && `Manzana ${suggestion.parsed_country_data.block}`}
                        {suggestion.parsed_country_data.block && suggestion.parsed_country_data.house && ' · '}
                        {suggestion.parsed_country_data.house && `Lote ${suggestion.parsed_country_data.house}`}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {isOpen && !isLoading && suggestions.length === 0 && value.length >= 3 && (
        <div className="absolute z-[1000] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-5 text-center">
          <p className="m-0 text-gray-500 text-[15px]">No encontramos direcciones con ese nombre</p>
        </div>
      )}
    </div>
  );
}
